import React, { useState, useEffect, useRef } from 'react';
import { X, Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import { format, isAfter, isBefore, isSameDay } from "date-fns";
import MetricCard from './MetricCard';

const CustomDateRangePicker = ({ 
  isOpen, 
  onClose, 
  onApply,
  calendarRef 
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [tempStartDate, setTempStartDate] = useState(null);
  const [tempEndDate, setTempEndDate] = useState(null);
  const [isSelectingStart, setIsSelectingStart] = useState(true);

  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];

  const daysOfWeek = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const formatDate = (date) => {
    if (!date) return '';
    return format(date, 'yyyy-MM-dd');
  };

  const formatDisplayDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return format(date, 'MMM dd, yyyy');
  };

  const handleDateSelect = (date) => {
    if (isSelectingStart || !tempStartDate) {
      setTempStartDate(date);
      setTempEndDate(null);
      setIsSelectingStart(false);
    } else {
      if (isBefore(date, tempStartDate)) {
        setTempStartDate(date);
        setTempEndDate(null);
      } else {
        setTempEndDate(date);
      }
    }
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(prevMonth => {
      const newMonth = new Date(prevMonth);
      newMonth.setMonth(newMonth.getMonth() + direction);
      return newMonth;
    });
  };

  const isToday = (date) => {
    const today = new Date();
    return date && isSameDay(date, today);
  };

  const isInRange = (date) => {
    if (!tempStartDate || !tempEndDate || !date) return false;
    return (isAfter(date, tempStartDate) || isSameDay(date, tempStartDate)) &&
           (isBefore(date, tempEndDate) || isSameDay(date, tempEndDate));
  };

  const isStartDate = (date) => {
    return tempStartDate && date && isSameDay(date, tempStartDate);
  };

  const isEndDate = (date) => {
    return tempEndDate && date && isSameDay(date, tempEndDate);
  };

  const applyDateRange = () => {
    if (tempStartDate && tempEndDate) {
      const formattedStart = formatDate(tempStartDate);
      const formattedEnd = formatDate(tempEndDate);
      onApply(`custom:${formattedStart}:${formattedEnd}`);
      onClose();
    }
  };

  const clearSelection = () => {
    setTempStartDate(null);
    setTempEndDate(null);
    setIsSelectingStart(true);
  };

  const days = getDaysInMonth(currentMonth);

  if (!isOpen) return null;

  return (
    <div
      ref={calendarRef}
      className="absolute top-10 right-0 z-20 bg-white rounded-lg shadow-lg border border-gray-200 w-72"
    >
      <div className="flex items-center justify-between p-2 border-b border-gray-100">
        <button
          type="button"
          onClick={() => navigateMonth(-1)}
          className="p-1 hover:bg-gray-50 rounded-full transition-colors"
        >
          <ChevronLeft className="w-3 h-3 text-gray-600" />
        </button>
        
        <span className="font-medium text-gray-900 text-sm">
          {months[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </span>
        
        <button
          type="button"
          onClick={() => navigateMonth(1)}
          className="p-1 hover:bg-gray-50 rounded-full transition-colors"
        >
          <ChevronRight className="w-3 h-3 text-gray-600" />
        </button>
      </div>

      <div className="px-2 py-1 bg-gray-50 text-xs text-gray-700">
        {isSelectingStart || !tempStartDate ? 
          'Select start date' : 
          (tempEndDate ? 
            `${formatDisplayDate(formatDate(tempStartDate))} — ${formatDisplayDate(formatDate(tempEndDate))}` :
            `Start: ${formatDisplayDate(formatDate(tempStartDate))} • Select end date`
          )
        }
      </div>

      <div className="grid grid-cols-7 gap-1 p-1 border-b border-gray-100">
        {daysOfWeek.map(day => (
          <div key={day} className="text-center text-xs font-medium text-gray-500 py-1">
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1 p-1">
        {days.map((date, index) => {
          const isStart = isStartDate(date);
          const isEnd = isEndDate(date);
          const inRange = isInRange(date);
          const today = isToday(date);
          
          return (
            <button
              key={index}
              type="button"
              onClick={() => date && handleDateSelect(date)}
              disabled={!date}
              className={`
                h-7 w-7 text-xs rounded-md transition-colors flex items-center justify-center
                ${!date ? 'invisible' : ''}
                ${isStart || isEnd
                  ? 'text-white font-medium' 
                  : inRange
                  ? 'text-gray-900'
                  : today
                  ? 'bg-gray-100 text-gray-900 font-medium border border-gray-300'
                  : 'text-gray-700 hover:bg-gray-50'
                }
              `}
              style={{
                backgroundColor: isStart || isEnd ? '#003366' : inRange ? '#e6f0ff' : undefined
              }}
            >
              {date?.getDate()}
            </button>
          );
        })}
      </div>

      <div className="flex justify-between items-center p-2 border-t border-gray-100">
        <button
          type="button"
          onClick={clearSelection}
          className="text-xs text-gray-500 hover:text-gray-700 transition-colors"
        >
          Clear
        </button>
        <div className="flex space-x-2">
          <button
            type="button"
            onClick={onClose}
            className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1 rounded transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={applyDateRange}
            disabled={!tempStartDate || !tempEndDate}
            className="text-xs text-white px-2 py-1 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ 
              backgroundColor: !tempStartDate || !tempEndDate ? '#9ca3af' : '#003366',
              borderColor: '#003366'
            }}
            onMouseEnter={(e) => {
              if (!e.target.disabled) {
                e.target.style.backgroundColor = '#004080';
              }
            }}
            onMouseLeave={(e) => {
              if (!e.target.disabled) {
                e.target.style.backgroundColor = '#003366';
              }
            }}
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

const ExpandedView = ({ 
  namespace, 
  metricName, 
  resourceNames, 
  api, 
  initialSelectedTime, 
  dashboardData, 
  onClose,
  metricData,
}) => {
  const [selectedTime, setSelectedTime] = useState(initialSelectedTime);
  const [showCalendar, setShowCalendar] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const calendarRef = useRef(null);
  
  const timeOptions = [
    { value: "1h", label: "1hr" },
    { value: "3h", label: "3hr" },
    { value: "12h", label: "12hr" },
    { value: "1d", label: "1d" },
    { value: "3d", label: "3d" },
    { value: "1w", label: "1w" },
    { value: "custom", label: "Custom" },
  ];
  useEffect(() => {
    if (selectedTime && selectedTime.startsWith("custom:")) {
      const [, startIso, endIso] = selectedTime.split(":");
      try {
        setStartDate(new Date(startIso));
        setEndDate(new Date(endIso));
      } catch (error) {
        console.error("Error parsing custom date range:", error);
      }
    }
  }, [selectedTime]);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleTimeOptionClick = (timeValue) => {
    if (timeValue === "custom") {
      setShowCalendar(true);
    } else {
      setSelectedTime(timeValue);
      setShowCalendar(false);
    }
  };

  const formatDateForDisplay = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatCustomDateRange = () => {
    if (selectedTime && selectedTime.startsWith("custom:")) {
      const [, startDateStr, endDateStr] = selectedTime.split(":");
      try {
        return `${formatDateForDisplay(startDateStr)} - ${formatDateForDisplay(endDateStr)}`;
      } catch (error) {
        console.error("Error formatting custom date range:", error);
        return "Custom Range";
      }
    }
    return "Custom";
  };
  const TimeFilter = () => (
    <div className="relative">
      <div className="flex items-center bg-gray-100 rounded-lg p-1 h-9">
        <Clock className="h-3 w-3 text-[#003366] ml-2 mr-1" />
        {timeOptions.map((option) => (
          <button
            key={option.value}
            onClick={() => handleTimeOptionClick(option.value)}
            className={`mx-1 px-2 py-1 text-xs font-medium rounded transition-colors duration-200 min-w-auto h-7 ${
              selectedTime === option.value ||
              (option.value === "custom" &&
                selectedTime &&
                selectedTime.startsWith("custom:"))
                ? "bg-[#003366] text-white"
                : "bg-transparent text-[#003366] hover:bg-black/10"
            }`}
            style={{
              textTransform: "none",
            }}
          >
            {option.value === "custom" &&
            selectedTime &&
            selectedTime.startsWith("custom:")
              ? formatCustomDateRange()
              : option.label}
          </button>
        ))}
      </div>

      {showCalendar && (
        <CustomDateRangePicker
          isOpen={showCalendar}
          onClose={() => setShowCalendar(false)}
          onApply={setSelectedTime}
          calendarRef={calendarRef}
        />
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-semibold text-gray-800">
              {namespace} - {metricName}
            </h2>
          </div>
          
          <button 
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            <X className="h-6 w-6 text-gray-600" />
          </button>
        </div>
        <div className="flex-grow p-6 overflow-y-auto">
          <MetricCard
            namespace={namespace}
            metricName={metricName}
            resourceNames={resourceNames}
            api={api}
            selectedTime={selectedTime}
            dashboardData={dashboardData}
            isExpanded={true}
            height={500}
            initialData={metricData}
            timeFilter={<TimeFilter />} 
          />
        </div>
      </div>
    </div>
  );
};

export default ExpandedView;